import { Team } from '@/types';

export interface SetLike {
  winnerId?: string | null;
  winner_id?: string | null;
  scoreA?: number;
  score_a?: number;
  scoreB?: number;
  score_b?: number;
  endedAt?: string | null;
  ended_at?: string | null;
}

export interface SetScoreColorResult {
  /** CSS color string for the winning team, or null for in-progress sets */
  color: string | null;
  /** True if the set is completed (has a winner) */
  isCompleted: boolean;
  /** True if team A won this set */
  teamAWon: boolean;
  /** True if team B won this set */
  teamBWon: boolean;
}

const NEUTRAL_COLOR = '#ffffff';

/**
 * Single source of truth for set score coloring.
 *
 * - Completed set: returns the winning team's configured color.
 * - In-progress set: returns null (caller should use white/neutral).
 *
 * Handles both snake_case (from DB) and camelCase (from app state) set fields.
 */
export function getSetScoreColor(
  set: SetLike,
  teamA: { id: string; color: string } | Team | null | undefined,
  teamB: { id: string; color: string } | Team | null | undefined,
): SetScoreColorResult {
  const winnerId = set.winnerId ?? set.winner_id ?? null;
  const isCompleted = winnerId !== null;

  if (!isCompleted || !teamA || !teamB) {
    return { color: null, isCompleted: false, teamAWon: false, teamBWon: false };
  }

  const teamAWon = winnerId === teamA.id;
  const teamBWon = winnerId === teamB.id;

  if (teamAWon) return { color: teamA.color, isCompleted: true, teamAWon: true, teamBWon: false };
  if (teamBWon) return { color: teamB.color, isCompleted: true, teamAWon: false, teamBWon: true };

  return { color: null, isCompleted: false, teamAWon: false, teamBWon: false };
}

/**
 * Returns the inline style object for a completed set score.
 * For completed sets, uses the winning team's color.
 * For in-progress sets, uses white/neutral.
 */
export function setScoreStyle(
  set: SetLike,
  teamA: { id: string; color: string } | Team | null | undefined,
  teamB: { id: string; color: string } | Team | null | undefined,
): React.CSSProperties {
  const { color } = getSetScoreColor(set, teamA, teamB);
  return { color: color ?? NEUTRAL_COLOR };
}

/**
 * Returns the CSS class for a set score.
 * For completed sets, no class is needed — the color comes from inline style.
 * For in-progress sets, returns a neutral text class.
 */
export function setScoreClassName(
  set: SetLike,
  teamA: { id: string; color: string } | Team | null | undefined,
  teamB: { id: string; color: string } | Team | null | undefined,
  baseClass = '',
): string {
  const { isCompleted } = getSetScoreColor(set, teamA, teamB);
  return isCompleted ? baseClass : `${baseClass} text-text-primary`.trim();
}
